import { Close } from '@mui/icons-material';
import CreateIcon from '@mui/icons-material/Create';
import {
  Alert,
  Box,
  Divider,
  Drawer,
  Grid,
  IconButton,
  Stack,
  styled,
  useTheme
} from '@mui/material';
import { formatPercent, parseDate } from '@vestwell-frontend/helpers';
import { Button, FormatPercent, Text } from '@vestwell-frontend/ui';
import { useWentworthBalanceGetRateOfReturn } from '@vestwell-unified-api/hooks';

import { format, startOfYear, subMonths } from 'date-fns';
import { FC, useEffect, useMemo, useState } from 'react';
import { useToggle } from 'react-use';
import { Bar, BarChart, Cell, ReferenceLine, XAxis } from 'recharts';

import { CustomRangeForm, Period } from '../CustomRangeForm.component';
import { ChartSkeleton } from './ChartSkeleton.component';
import { GridRow } from './GridRow.component.component';

export type RateOfReturnDrawerProps = {
  employeeId: number;
};

const StyledAlert = styled(Alert)(() => ({
  marginTop: 0
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.grey100.main,
    paddingTop: theme.spacing(8),
    width: 540
  },
  [theme.breakpoints.down('md')]: {
    '& .MuiDrawer-paper': {
      height: '100%',
      paddingTop: 0,
      width: '100%'
    },
    zIndex: 1202
  }
}));

const StyledHeader = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  margin: 0,
  marginBottom: theme.spacing(2),
  padding: 0
}));

const StyledCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.white.main,
  borderRadius: theme.shape.borderRadius,
  boxShadow: 'none',
  marginBottom: theme.spacing(2),
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  padding: theme.spacing(2)
}));

const formatRange = (period: Period) =>
  `${format(parseDate(period.startDate), 'MM/dd/yyyy')} -  ${period.endDate ? format(parseDate(period.endDate), 'MM/dd/yyyy') : 'Today'}`;

export const RateOfReturnDrawer: FC<RateOfReturnDrawerProps> = props => {
  const theme = useTheme();
  const [isEdit, toggleIsEdit] = useToggle(false);
  const [isOpen, toggleIsOpen] = useToggle(false);

  const [selectedPeriod, setPeriod] = useState<Period>();
  const defaultPeriod = {
    endDate: new Date(),
    startDate: startOfYear(new Date())
  };

  const period = useMemo(
    () => selectedPeriod ?? defaultPeriod,
    [selectedPeriod]
  );

  const rateOfReturn = useWentworthBalanceGetRateOfReturn(
    {
      endDate: period.endDate
        ? format(period.endDate, 'MM-dd-yyyy')
        : undefined,
      participantId: props.employeeId,
      startDate: format(period.startDate, 'MM-dd-yyyy')
    },
    {
      query: {
        enabled: !!props.employeeId,
        keepPreviousData: true
      }
    }
  );

  const isLoading = rateOfReturn.isLoading || rateOfReturn.isFetching;

  const rows = useMemo(
    () => [
      {
        dateRange: 'Year to Date',
        dateRangeLabel: 'YTD',
        dates: {
          endDate: new Date(),
          startDate: startOfYear(new Date())
        },
        fill: theme.palette.blue70.main,
        value: rateOfReturn.data?.rateOfReturn?.[0]?.yearToDate
      },
      {
        dateRange: 'Last 12 Months',
        dateRangeLabel: '12M',
        dates: {
          endDate: subMonths(new Date(), 12),
          startDate: new Date()
        },
        fill: theme.palette.primary.main,
        value: rateOfReturn.data?.rateOfReturn?.[1]?.last12months
      },
      {
        dateRange: 'Last 6 Months',
        dateRangeLabel: '6M',
        dates: {
          endDate: subMonths(new Date(), 6),
          startDate: new Date()
        },
        fill: theme.palette.blue50.main,
        value: rateOfReturn.data?.rateOfReturn?.[2]?.last6months
      },
      {
        dateRange: 'Last 3 Months',
        dateRangeLabel: '3M',
        dates: {
          endDate: subMonths(new Date(), 3),
          startDate: new Date()
        },
        fill: theme.palette.primary.light,
        value: rateOfReturn.data?.rateOfReturn?.[3]?.last3months
      }
    ],
    [rateOfReturn.data]
  );

  const chartBars = useMemo(
    () =>
      selectedPeriod && rateOfReturn.data?.customDateRangeRateOfReturn
        ? [
            ...rows,
            {
              dateRange: 'Custom range',
              dateRangeLabel: 'CR',
              dates: period,
              fill: theme.palette.blue30.main,
              value: rateOfReturn.data?.customDateRangeRateOfReturn
            }
          ]
        : rows,

    [rateOfReturn?.data]
  );

  useEffect(() => {
    if (isEdit) {
      toggleIsEdit();
    }
  }, [rateOfReturn?.data]);

  return (
    <>
      <Button onClick={toggleIsOpen} variant='text'>
        Change Range
      </Button>

      <StyledDrawer anchor='right' onClose={toggleIsOpen} open={isOpen}>
        <StyledHeader>
          <Grid
            alignItems='center'
            container
            direction='row'
            justifyContent='space-between'
            m={0}
            px={2}
            py={2.5}>
            <Text m={0} variant='h6'>
              Personalized Rate of Return
            </Text>
            <IconButton aria-label='Close' onClick={toggleIsOpen}>
              <Close />
            </IconButton>
          </Grid>
        </StyledHeader>

        <StyledCard>
          <Stack spacing={2}>
            <Text fontWeight='normal' variant='subtitle2'>
              The Personalized Rate of Return (RoR) for a 401(k) account
              measures the account's performance over a specified period. This
              metric accounts for the investment earnings and factors in the
              timing of contributions, withdrawals, loans, and fees. It provides
              a customized performance indicator that reflects the individual
              growth of each saver’s account. <br />
              <br />
              For periods shorter than a year, the cumulative return for the
              401(k) account is displayed. For periods of a year or longer, the
              annualized return is shown. This approach facilitates accurate
              comparison of the account's performance across different time
              frames.
            </Text>
          </Stack>
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='center'
            pb={2}
            pt={2}>
            {!chartBars.every(row => !row.value) ? (
              <>
                {isLoading && <ChartSkeleton />}
                {!isLoading && (
                  <BarChart
                    data={chartBars.filter(row => row.value)}
                    data-testid='rate-of-return-bar-chart'
                    height={330}
                    margin={{
                      top: 16
                    }}
                    width={320}>
                    <Bar
                      dataKey='value'
                      fill={theme.palette.blue70.main}
                      label={{
                        fill: theme.palette.grey700.main,
                        fontSize: theme.typography.subtitle2.fontSize,
                        formatter: formatPercent,
                        offset: 8,
                        position: 'top'
                      }}
                      radius={[2, 2, 2, 2]}
                      width={40}>
                      {chartBars
                        .filter(row => row.value)
                        .map((entry, index) => (
                          <Cell fill={entry.fill} key={`cell-${index}`} />
                        ))}
                    </Bar>
                    <XAxis
                      axisLine={false}
                      dataKey='dateRangeLabel'
                      tick={{ fontWeight: 700 }}
                      tickLine={false}
                    />
                    <ReferenceLine stroke={theme.palette.grey100.main} y={0} />
                  </BarChart>
                )}
              </>
            ) : (
              <Text>No data available to display.</Text>
            )}
          </Stack>
          {rows.some(row => !row.value) && (
            <StyledAlert role='note' severity='warning'>
              The Rate of Return for certain ranges may not be available, likely
              due to limited investment activity.
            </StyledAlert>
          )}
          <Stack divider={<Divider />} spacing={2}>
            {rows.map(row => (
              <GridRow
                dateRange={formatRange(row.dates)}
                dateRangeName={row.dateRange}
                key={row.dateRange + `${row.value}`}
                loading={!rateOfReturn.data && isLoading}
                value={row.value as number}
              />
            ))}
          </Stack>
        </StyledCard>
        <StyledCard>
          <Text fontWeight='bold' mb={2.5}>
            Custom Range
          </Text>
          {selectedPeriod && !isEdit && (
            <>
              <Stack flexDirection='row' justifyContent='space-between'>
                <Text m={0}>{formatRange(selectedPeriod)}</Text>
                <Text m={0}>
                  {rateOfReturn?.data?.customDateRangeRateOfReturn ? (
                    <FormatPercent
                      value={rateOfReturn?.data?.customDateRangeRateOfReturn}
                    />
                  ) : (
                    'Not available'
                  )}
                </Text>
              </Stack>
              <Text fontWeight='normal' mb={2.5} variant='subtitle2'>
                Custom Range
              </Text>
            </>
          )}
          {isEdit ? (
            <CustomRangeForm
              cancel={toggleIsEdit}
              change={setPeriod}
              disabled={isLoading}
              period={selectedPeriod}
              submitTitle='Calculate'
            />
          ) : (
            <Grid
              alignItems='end'
              container
              direction='row'
              justifyContent='end'>
              <Button
                color='primary'
                endIcon={<CreateIcon />}
                onClick={toggleIsEdit}>
                {selectedPeriod ? 'Change' : 'Enter'} Date Range
              </Button>
            </Grid>
          )}
        </StyledCard>
      </StyledDrawer>
    </>
  );
};
